import { defineStoryblok } from "@trydig/storyblok";
import { rpc } from "#rpc";
import { AccordionBlok, AccordionItemBlok } from "~/components/bloks/AccordionBlok";
import { ArticleCardBlok } from "~/components/bloks/ArticleCardBlok";
import { ArticleListingBlok } from "~/components/bloks/ArticleListingBlok";
import { ArticleListingPageBlok } from "~/components/bloks/ArticleListingPageBlok";
import { ArticlePageBlok } from "~/components/bloks/ArticlePageBlok";
import { BundleBlok } from "~/components/bloks/BundleBlok";
import { CardGridBlok } from "~/components/bloks/CardGridBlok";
import { CollectionCardBlok } from "~/components/bloks/CollectionCardBlok";
import { CollectionListingPageBlok } from "~/components/bloks/CollectionListingPageBlok";
import { CollectionPageBlok } from "~/components/bloks/CollectionPageBlok";
import { ColoredSectionBlok } from "~/components/bloks/ColoredSectionBlok";
import { ColumnBlok } from "~/components/bloks/ColumnBlok";
import { ColumnsBlok } from "~/components/bloks/ColumnsBlok";
import { ContainerBlok } from "~/components/bloks/ContainerBlok";
import { CorrectionsBlok } from "~/components/bloks/CorrectionsBlok";
import { DealerListingsBlok } from "~/components/bloks/DealerListingsBlok";
import { DoubleHeroBlok } from "~/components/bloks/DoubleHeroBlok";
import { FAQsBlok } from "~/components/bloks/FAQsBlok";
import { HeroCardBlok } from "~/components/bloks/HeroCardBlok";
import { HeroSliderBlok } from "~/components/bloks/HeroSliderBlok";
import { LinkBlok } from "~/components/bloks/LinkBlok";
import { MarqueeBlok } from "~/components/bloks/MarqueeBlok";
import { MediaBlok } from "~/components/bloks/MediaBlok";
import { MediaCardBlok } from "~/components/bloks/MediaCardBlok";
import { MediaGalleryBlok } from "~/components/bloks/MediaGalleryBlok";
import { PageBlok } from "~/components/bloks/PageBlok";
import { ProductGalleryWithTabsBlok } from "~/components/bloks/ProductGalleryWithTabsBlok";
import { QuoteBlok } from "~/components/bloks/QuoteBlok";
import { RichTextBlok } from "~/components/bloks/RichTextBlok";
import { TagBlok } from "~/components/bloks/TagBlok";
import { TagCloudBlok } from "~/components/bloks/TagCloudBlok";
import { TaggedImageBlok } from "~/components/bloks/TaggedImageBlok";
import { TextBannerBlok } from "~/components/bloks/TextBannerBlok";
import type { TaggedImageStoryblokExtended } from "~/generated/sb/addtional-types";
import type { TypescriptGeneratorStoryblok } from "~/generated/sb/blok-types";

export type AllBloks = NonNullable<TypescriptGeneratorStoryblok["body"]>[number];

const NotFound = () => {
	return null;
};

defineStoryblok<AllBloks>({
	bloks: {
		accordionItem: AccordionItemBlok,
		accordion: AccordionBlok,
		Article: ArticlePageBlok,
		ArticleCard: {
			blok: ArticleCardBlok,
			fetcher: rpc.story.bloks.articleCard,
			getKey: ({ blok }) => blok.article,
		},
		ArticleListing: {
			blok: ArticleListingBlok,
			fetcher: rpc.story.bloks.articleListing,
			getKey: ({ blok }) => blok.tags,
		},
		articleListingPage: {
			blok: ArticleListingPageBlok,
			fetcher: rpc.story.bloks.featuredArticle,
			getKey: ({ blok }) => blok?.featuredArticle,
		},
		bundle: {
			blok: BundleBlok,
			fetcher: rpc.story.bloks.bundle,
			getKey: ({ blok }) => blok._uid,
		},
		cardGrid: CardGridBlok,
		CollectionCard: {
			blok: CollectionCardBlok,
			fetcher: rpc.story.bloks.collection,
			getKey: ({ blok }) => blok.collection,
		},
		collectionListingPage: {
			blok: CollectionListingPageBlok,
			fetcher: rpc.story.bloks.collectionCards,
			getKey: ({ blok }) => blok?.collections,
		},
		collectionPage: CollectionPageBlok,
		coloredSection: ColoredSectionBlok,
		corrections: {
			blok: CorrectionsBlok,
			fetcher: rpc.story.bloks.corrections,
			getKey: ({ blok }) => blok._uid,
		},
		column: ColumnBlok,
		columns: ColumnsBlok,
		container: ContainerBlok,
		doubleHero: DoubleHeroBlok,
		FAQs: FAQsBlok,
		tag: TagBlok,
		heroCard: HeroCardBlok,
		link: LinkBlok,
		heroSlider: HeroSliderBlok,
		marquee: MarqueeBlok,
		media: MediaBlok,
		mediaCard: MediaCardBlok,
		mediaGallery: MediaGalleryBlok,
		page: PageBlok,
		textBanner: TextBannerBlok,
		productGalleryWithTabs: {
			blok: ProductGalleryWithTabsBlok,
			fetcher: rpc.story.bloks.productGalleryWithTabs,
			getKey: ({ blok }) => blok.Tabs,
		},
		Quote: QuoteBlok,
		richText: RichTextBlok,
		snackbar: NotFound,
		tagCloud: TagCloudBlok,
		taggedImage: {
			blok: TaggedImageBlok,
			fetcher: rpc.story.bloks.taggedImage,
			getKey: ({ blok }) =>
				(blok as TaggedImageStoryblokExtended | undefined)?.products?.products?.map(
					(p) => p.product,
				) || blok._uid,
		},
		dealerListings: {
			blok: DealerListingsBlok,
			fetcher: rpc.story.bloks.dealerListings,
			getKey: ({ blok }) => blok._uid,
		},
		productListingPage: NotFound,
		TYPESCRIPT_GENERATOR: NotFound,
	},
});
